import { useCallback, useMemo } from 'react';
import { AsyncSelectField, type AsyncSelectOption } from './async_select_field.js';
import type { FieldShellExtras } from './field_shell.js';

/**
 * O CAMPO DE SELEÇÃO COM BUSCA de catálogo FECHADO — o meio-termo entre o
 * `StaticSelectField` (lista inteira, sem busca) e o `AsyncSelectField` (busca
 * remota, catálogo que não cabe). As opções já estão na mão, mas são dezenas:
 * o select do Motivo do checkout chega com 50 itens codificados por centro de
 * custo, abria para cima e estourava o topo do modal (#3217 C7).
 *
 * É o `AsyncSelectField` com a busca resolvida aqui: `minChars: 0` lista o
 * catálogo inteiro ao abrir, e o filtro roda no render (array direto, sem
 * Promise), então não existe o estado "buscando". A comparação ignora acento
 * e caixa — "reuniao" acha "Reunião".
 */

export interface SearchableSelectFieldProps extends FieldShellExtras {
  /** Nome visível E acessível do campo. */
  label: string;
  /** Vazio = nada escolhido: o input ocioso mostra o `placeholder`. */
  value: string;
  onChange: (value: string) => void;
  options: readonly AsyncSelectOption[];
  placeholder?: string;
  /** Filtro sem resultado. */
  emptyText: string;
  id?: string;
  disabled?: boolean;
}

/** Sem acento e em minúsculas — a chave de comparação dos dois lados. */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function filterSelectOptions(
  options: readonly AsyncSelectOption[],
  term: string,
): AsyncSelectOption[] {
  const needle = fold(term.trim());
  if (needle === '') return [...options];
  return options.filter(
    (option) =>
      fold(option.label).includes(needle) ||
      (option.description !== undefined && fold(option.description).includes(needle)),
  );
}

export function SearchableSelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  emptyText,
  id,
  disabled,
  adornment,
  adornmentLabel,
  message,
  messageTone,
  required,
}: Readonly<SearchableSelectFieldProps>) {
  const onSearch = useCallback((term: string) => filterSelectOptions(options, term), [options]);
  const selectedLabel = useMemo(
    () => options.find((option) => option.value === value)?.label ?? '',
    [options, value],
  );
  return (
    <AsyncSelectField
      label={label}
      adornment={adornment}
      adornmentLabel={adornmentLabel}
      message={message}
      messageTone={messageTone}
      value={value}
      selectedLabel={selectedLabel}
      {...(placeholder === undefined ? {} : { placeholder })}
      /* `minChars: 0` nunca mostra o prompt, e o filtro local nunca "carrega":
         as três frases de estado colapsam na única que pode aparecer. */
      promptText={emptyText}
      loadingText={emptyText}
      emptyText={emptyText}
      minChars={0}
      debounceMs={0}
      onSearch={onSearch}
      /* Só o valor: o rótulo o próprio campo resolve pelo catálogo, não há o
         que o dono guardar. */
      onChange={(next) => {
        onChange(next);
      }}
      {...(disabled === undefined ? {} : { disabled })}
      {...(id === undefined ? {} : { id })}
      {...(required ? { required } : {})}
    />
  );
}
